import React, { useState, useRef, useCallback } from 'react';
import { API } from '@/App';
import { Link2, Check, X, Loader2, ChevronDown, ChevronUp, Download, BarChart3, Grid3X3, Target, GitFork, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { PipelineUploadZone } from './pipeline/PipelineUploadZone.jsx';
import { RecommendationsSection } from './pipeline/RecommendationsSection.jsx';

const STEPS = [
  { key: 'step1', label: 'ANOVA Analysis', icon: BarChart3, desc: 'Treatment effects for all traits' },
  { key: 'step2', label: 'Genetic Parameters', icon: Sparkles, desc: 'H², GCV, PCV, GA estimation' },
  { key: 'step3', label: 'Correlation Analysis', icon: Grid3X3, desc: 'Pearson correlation matrix' },
  { key: 'step4', label: 'Path Analysis', icon: GitFork, desc: 'Direct & indirect effects on yield' },
  { key: 'step5', label: 'Selection Index', icon: Target, desc: 'Multi-trait selection ranking' },
  { key: 'step6', label: 'Master Report', icon: Link2, desc: 'Linked tables & figures' },
];

const TABLE_TITLES = {
  table_a: 'Table A. Master Genetic Parameters — Variability & Selection Efficiency',
  table_b: 'Table B. Correlation × Path Combined — Direct & Indirect Effects on Yield',
  table_c: 'Table C. Selection Summary — Genotype Ranking by Yield & Multi-trait Index',
  table_d: 'Table D. Trait Importance Ranking — Selection Criteria Priorities',
};

const FIG_TITLES = {
  figure_a: 'Figure A. Genetic Variability Radar Chart (H², GCV, GA)',
  figure_b: 'Figure B. Correlation Heatmap with Significance',
  figure_c: 'Figure C. Path Diagram — Effects on Yield',
  figure_d: 'Figure D. Selection Scatter Plot (Yield vs Index)',
  figure_e: 'Figure E. Trait Contribution to Yield',
};

function StepIndicator({ step, stepData, index }) {
  const status = stepData?.status || 'pending';
  const Icon = step.icon;
  return (
    <div className="flex items-start gap-3" data-testid={`pipeline-${step.key}`}>
      <div className="flex flex-col items-center">
        <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{
          background: status === 'complete' ? '#1B4332' : status === 'running' ? '#40916C' : status === 'error' ? '#E53E3E' : '#E2E8F0',
        }}>
          {status === 'complete' ? <Check size={14} className="text-white" /> :
           status === 'running' ? <Loader2 size={14} className="text-white animate-spin" /> :
           status === 'error' ? <X size={14} className="text-white" /> :
           <span className="text-xs font-bold" style={{ color: '#718096' }}>{index + 1}</span>}
        </div>
        {index < 5 && <div className="w-0.5 h-6 mt-1" style={{ background: status === 'complete' ? '#1B4332' : '#E2E8F0' }} />}
      </div>
      <div className="pb-4 min-w-0">
        <p className="font-body text-sm font-semibold" style={{ color: status === 'complete' ? 'var(--green-900)' : status === 'error' ? '#E53E3E' : 'var(--text-dark)' }}>
          {step.label}
        </p>
        {status === 'complete' && stepData?.summary && (
          <p className="font-body text-xs mt-0.5 truncate" style={{ color: 'var(--text-medium)', maxWidth: 400 }}>{stepData.summary}</p>
        )}
        {status === 'error' && <p className="font-body text-xs mt-0.5" style={{ color: '#E53E3E' }}>{stepData?.error}</p>}
        {status === 'pending' && <p className="font-body text-xs mt-0.5" style={{ color: 'var(--text-light)' }}>{step.desc}</p>}
      </div>
    </div>
  );
}

function LinkedTable({ title, rows, tableKey }) {
  const [open, setOpen] = useState(true);
  if (!rows || rows.length === 0) return null;
  const cols = Object.keys(rows[0]);
  const colorCell = (val) => {
    const s = String(val);
    if (s === 'Yes' || s === 'Advance' || s === 'High') return { bg: '#E8F5E9', color: '#1B5E20' };
    if (s === 'Moderate' || s === 'Evaluate' || s === 'Medium') return { bg: '#FFF3E0', color: '#E65100' };
    if (s === 'No' || s === 'Reject' || s === 'Low') return { bg: '#FFEBEE', color: '#B71C1C' };
    return {};
  };
  return (
    <div className="bg-white rounded-xl mb-4 overflow-hidden" style={{ border: '1px solid var(--border-color)' }} data-testid={`linked-${tableKey}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 transition-colors"
        style={{ background: '#F0F7F3', borderBottom: open ? '1px solid var(--border-color)' : 'none' }}>
        <span className="font-body text-sm font-semibold" style={{ color: 'var(--green-900)' }}>{title}</span>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr style={{ background: '#1B4332' }}>
                {cols.map(c => (
                  <th key={c} className="px-3 py-2 text-center font-body whitespace-nowrap" style={{ fontSize: 11, fontWeight: 600, color: 'white' }}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri} style={{ background: ri % 2 === 0 ? 'white' : '#F8FAF9' }}>
                  {cols.map((c, ci) => {
                    const val = row[c];
                    const cc = colorCell(val);
                    return (
                      <td key={ci} className="px-3 py-1.5 text-center border-b" style={{ borderColor: '#E2E8F0', fontSize: 11, ...cc }}>
                        {typeof val === 'number' ? (
                          <span className="font-mono">{Number.isInteger(val) ? val : val.toFixed(4)}</span>
                        ) : (
                          <span className={cc.color ? 'font-bold' : 'font-body'}>{String(val)}</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function LinkedFigure({ title, b64, figKey }) {
  const [open, setOpen] = useState(true);
  if (!b64) return null;
  return (
    <div className="bg-white rounded-xl mb-4 overflow-hidden" style={{ border: '1px solid var(--border-color)' }} data-testid={`linked-${figKey}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 transition-colors"
        style={{ background: '#F0F7F3', borderBottom: open ? '1px solid var(--border-color)' : 'none' }}>
        <span className="font-body text-sm font-semibold" style={{ color: 'var(--green-900)' }}>{title}</span>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && (
        <div className="p-4 text-center">
          <img src={`data:image/png;base64,${b64}`} alt={title} className="max-w-full mx-auto rounded-lg" style={{ maxHeight: 500 }} />
          <p className="font-body text-xs mt-2 italic" style={{ color: 'var(--text-light)' }}>{title}</p>
        </div>
      )}
    </div>
  );
}


export default function BreedingPipeline({ onClose }) {
  const [file, setFile] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [currentStep, setCurrentStep] = useState(-1);
  const [depVar, setDepVar] = useState('');
  const [depVarOverride, setDepVarOverride] = useState('');
  const [enhancedText, setEnhancedText] = useState('');
  const [enhancing, setEnhancing] = useState(false);
  const [exporting, setExporting] = useState(null);
  const fileRef = useRef(null);

  const _sf = (v, d = 2) => typeof v === 'number' ? v.toFixed(d) : String(v ?? '-');

  const enhanceText = async () => {
    if (!result?.pipeline_id) return;
    setEnhancing(true);
    try {
      const { data } = await axios.post(`${API}/pipeline/enhance-results`,
        { pipeline_id: result.pipeline_id }, { withCredentials: true });
      if (data.enhanced_text) {
        setEnhancedText(data.enhanced_text);
        toast.success('Enhanced Results & Discussion generated!');
      } else {
        toast.error(data.error || 'Enhancement failed');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Enhancement failed');
    }
    setEnhancing(false);
  };

  const exportReport = async (fmt) => {
    if (!result?.pipeline_id) return;
    setExporting(fmt);
    try {
      const formData = new FormData();
      formData.append('format', fmt);
      const response = await axios.post(`${API}/pipeline/export/${result.pipeline_id}`, formData,
        { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `BKAgriD_Pipeline_Report.${fmt}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} report downloaded!`);
    } catch (e) {
      toast.error('Export failed');
    }
    setExporting(null);
  };

  const runPipeline = async (selectedFile) => {
    const f = selectedFile || file;
    if (!f) return toast.error('Select a file first');
    setRunning(true);
    setResult(null);
    setCurrentStep(0);

    // Simulate step progress
    const stepTimer = setInterval(() => {
      setCurrentStep(prev => Math.min(prev + 1, 5));
    }, 800);

    try {
      const formData = new FormData();
      formData.append('file', f);
      if (depVarOverride) formData.append('dep_var', depVarOverride);

      const { data } = await axios.post(`${API}/pipeline/run`, formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });

      clearInterval(stepTimer);
      setCurrentStep(6);
      setResult(data);
      setDepVar(data.dep_var || '');
      toast.success('Pipeline complete! All 6 steps finished.');
    } catch (e) {
      clearInterval(stepTimer);
      toast.error(e.response?.data?.detail || 'Pipeline failed');
    }
    setRunning(false);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const f = e.dataTransfer.files?.[0];
    if (f && f.name.match(/\.(csv|xlsx|xls)$/i)) {
      setFile(f);
      toast.success(`${f.name} loaded`);
    }
  }, []);

  const completedSteps = result ? Object.values(result.steps || {}).filter(s => s.status === 'complete').length : 0;
  const progress = running ? Math.round((currentStep / 6) * 100) : (result ? Math.round((completedSteps / 6) * 100) : 0);

  return (
    <div className="space-y-4 fade-in" data-testid="breeding-pipeline">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl" style={{ color: 'var(--green-900)' }}>Complete Breeding Analysis Pipeline</h2>
          <p className="font-body text-xs mt-0.5" style={{ color: 'var(--text-medium)' }}>6-step automated analysis: ANOVA → Heritability → Correlation → Path → Selection → Report</p>
        </div>
        {progress > 0 && (
          <div className="text-right">
            <span className="font-mono text-lg font-bold" style={{ color: 'var(--green-900)' }}>{progress}%</span>
            <p className="font-body text-[10px]" style={{ color: 'var(--text-light)' }}>{completedSteps}/6 steps</p>
          </div>
        )}
      </div>

      {/* Upload Zone (if no result yet) — Sub-Batch 3M: extracted to
          components/pipeline/PipelineUploadZone.jsx for separation of
          concerns.  Pure visual extraction; zero behavioural change. */}
      {!result && (
        <div className="space-y-3">
          <PipelineUploadZone
            file={file}
            setFile={setFile}
            fileRef={fileRef}
            depVarOverride={depVarOverride}
            setDepVarOverride={setDepVarOverride}
            handleDrop={handleDrop}
          />

          <button
            onClick={() => runPipeline()}
            disabled={!file || running}
            className="w-full font-body btn-press transition-all disabled:opacity-50"
            style={{
              height: 52, borderRadius: 'var(--radius-md)', fontWeight: 600, fontSize: 15,
              color: 'white', background: 'linear-gradient(135deg, #2D6A4F, #1B4332)', border: 'none',
            }}
            data-testid="run-pipeline-btn"
          >
            {running ? (
              <span className="flex items-center justify-center gap-2"><Loader2 size={16} className="animate-spin" /> Running Pipeline...</span>
            ) : (
              <span className="flex items-center justify-center gap-2"><Link2 size={16} /> Run Complete Breeding Analysis Pipeline — 6 Steps</span>
            )}
          </button>
        </div>
      )}

      {/* Step Progress */}
      {(running || result) && (
        <div className="bg-white rounded-xl p-5" style={{ border: '1px solid var(--border-color)' }}>
          <h3 className="font-body text-sm font-bold mb-3" style={{ color: 'var(--green-900)' }}>Pipeline Progress</h3>
          {STEPS.map((step, i) => {
            let stepData = result?.steps?.[step.key];
            if (!stepData && running) {
              stepData = i < currentStep ? { status: 'complete', summary: '' } :
                         i === currentStep ? { status: 'running' } : { status: 'pending' };
            }
            return <StepIndicator key={step.key} step={step} stepData={stepData || { status: 'pending' }} index={i} />;
          })}
        </div>
      )}

      {/* Dep var info */}
      {result?.dep_var && (
        <div className="rounded-lg px-4 py-2.5 flex items-center gap-2" style={{ background: '#F0FFF4', border: '1px solid #B7E4C7' }} data-testid="dep-var-info">
          <Target size={14} style={{ color: '#1B4332' }} />
          <span className="font-body text-xs" style={{ color: '#1B4332' }}>
            Using <b>{result.dep_var}</b> as dependent variable for Path Analysis
            {result.dep_var_auto && ' (auto-detected)'}
          </span>
        </div>
      )}

      {/* Linked Tables */}
      {result?.linked_tables && Object.keys(result.linked_tables).length > 0 && (
        <div>
          <h3 className="font-body text-sm font-bold mb-2" style={{ color: 'var(--green-900)' }}>Linked Tables — Combined Multi-Analysis Results</h3>
          {Object.entries(TABLE_TITLES).map(([key, title]) => (
            <LinkedTable key={key} title={title} rows={result.linked_tables[key]} tableKey={key} />
          ))}
        </div>
      )}

      {/* Linked Figures */}
      {result?.linked_figures && Object.keys(result.linked_figures).length > 0 && (
        <div>
          <h3 className="font-body text-sm font-bold mb-2" style={{ color: 'var(--green-900)' }}>Linked Figures — Integrated Visualizations</h3>
          {Object.entries(FIG_TITLES).map(([key, title]) => (
            <LinkedFigure key={key} title={title} b64={result.linked_figures[key]} figKey={key} />
          ))}
        </div>
      )}

      {/* AI Results & Discussion */}
      {result?.results_text && (
        <div className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid var(--border-color)' }} data-testid="pipeline-results-text">
          <div className="px-4 py-3 flex items-center justify-between" style={{ background: '#F0F7F3', borderBottom: '1px solid var(--border-color)' }}>
            <span className="font-body text-sm font-bold" style={{ color: 'var(--green-900)' }}>
              {enhancedText ? 'AI Enhanced — Results & Discussion' : 'AI Draft — Results & Discussion'}
            </span>
            <div className="flex items-center gap-2">
              <button onClick={enhanceText} disabled={enhancing}
                className="flex items-center gap-1 font-body btn-press text-xs font-semibold px-3 py-1.5 rounded-md transition-all"
                style={{ background: 'var(--green-900)', color: 'white' }}
                data-testid="enhance-results-btn">
                {enhancing ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
                {enhancedText ? 'Regenerate' : 'Enhance with AI'}
              </button>
            </div>
          </div>
          <div className="px-4 py-4">
            <p className="font-body text-sm leading-relaxed" style={{ color: 'var(--text-dark)' }}>
              {enhancedText || result.results_text}
            </p>
          </div>
        </div>
      )}

      {/* Breeding Recommendations — Sub-Batch 3M: extracted to
          components/pipeline/RecommendationsSection.jsx.  Pure visual
          extraction; zero behavioural change. */}
      <RecommendationsSection recommendations={result?.recommendations} />

      {/* Export + Actions */}
      {result && (
        <div className="flex items-center gap-3 flex-wrap pt-4" style={{ borderTop: '1px solid var(--border-color)' }} data-testid="pipeline-actions">
          <span className="font-body text-xs font-bold" style={{ color: 'var(--green-900)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Export Master Report</span>
          {['pdf', 'docx', 'xlsx'].map(fmt => (
            <button key={fmt} onClick={() => exportReport(fmt)} disabled={exporting}
              className="flex items-center gap-1.5 font-body btn-press text-xs font-semibold px-4 py-2 rounded-lg transition-all disabled:opacity-50"
              style={{ background: 'var(--green-700)', color: 'white' }}
              data-testid={`pipeline-export-${fmt}`}>
              {exporting === fmt ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
              {fmt.toUpperCase()}
            </button>
          ))}
          <button onClick={() => { setResult(null); setFile(null); setCurrentStep(-1); setEnhancedText(''); }}
            className="font-body btn-press px-4 py-2 rounded-lg text-xs font-semibold transition-all ml-auto"
            style={{ border: '1px solid var(--border-color)', color: 'var(--text-medium)', background: 'white' }}
            data-testid="pipeline-new-btn">
            New Pipeline
          </button>
        </div>
      )}
    </div>
  );
}
